// ============================================================
//  MASAMADREAM — Notifications Card + Web Notifications
//  Sistema robusto con persistencia en localStorage
// ============================================================

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PROCESSES } from './data.js';

const NOTIF_STORAGE_KEY = 'masagotchi_notifications';

const ICONS = {
  'Mezcla Inicial':       '🥣', 'Alimentar':             '🌾',
  'Alimentar y Verificar':'🧪', 'Pliegues':              '🤲',
  'Formación de Hogazas': '🫓', 'Reposo Inicial':        '💤',
  'Sacar y Reposar':      '⏱️', 'Precalentar Horno':     '🔥',
  'Hornear Tapado':       '🍳', 'Hornear Destapado':     '🥐',
  'Reposo Final':         '🎉', 'Pesar Ingredientes':    '⚖️',
  'Mezcla Inicial':       '🫙', 'Agregar Harina y Sal':  '🧂',
  default:                '📨',
};

function icon(action) { return ICONS[action] || ICONS.default; }

function fmt(iso, offsetHours) {
  const d = new Date(new Date(iso).getTime() + offsetHours * 3600000);
  return d.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit', hour12: true });
}

// ── Storage: Save/Load scheduled notifications ───────────────────
function _getStoredNotifs() {
  try {
    return JSON.parse(localStorage.getItem(NOTIF_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

function _saveStoredNotifs(notifs) {
  try {
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(notifs));
  } catch (e) {
    console.warn('[MM] Failed to save notifications:', e);
  }
}

function hashCode(str) {
  let hash = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    let chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return Math.abs(hash);
}

function _saveNotificationsForProcess(processId, activities, startDate) {
  const existing = _getStoredNotifs().filter(n => n.processId !== processId);
  
  activities.forEach(act => {
    const scheduledAt = new Date(startDate).getTime() + act.timeOffset * 3600000;
    if (scheduledAt > Date.now()) {
      existing.push({
        id: hashCode(`${processId}_${act.id}`), // Numeric ID for Capacitor
        processId,
        activityId: act.id,
        day: act.day,
        action: act.action,
        emoji: act.emoji,
        description: act.description,
        scheduledAt,
        fired: false,
      });
    }
  });
  
  _saveStoredNotifs(existing);
}

// ── Render right card ───────────────────────────────────────-
export function renderNotifications(process) {
  const container = document.getElementById('mm-notifications-body');
  if (!container) return;

  const pData = PROCESSES[process.type];
  if (!pData) return;

  const now   = Date.now();
  const start = new Date(process.startDate).getTime();

  // Split by day, mark each as past/upcoming
  const byDay = {};
  pData.activities.forEach(act => {
    const scheduledAt = start + act.timeOffset * 3600000;
    const state = scheduledAt < now ? 'past' : 'upcoming';
    (byDay[act.day] = byDay[act.day] || []).push({ ...act, scheduledAt, state });
  });

  // Count upcoming
  const upcomingCount = pData.activities.filter(a =>
    start + a.timeOffset * 3600000 > now
  ).length;

  // Update card header badge
  const headerEl = document.querySelector('#mm-card-notifications .mm-card-header .mm-card-icon');
  if (headerEl) {
    headerEl.textContent = upcomingCount > 0 ? `🔔 ${upcomingCount}` : '🔕';
  }

  container.innerHTML = '';

  Object.entries(byDay).forEach(([day, acts]) => {
    const group = document.createElement('div');
    group.className = 'mm-notif-day';

    const dayLabel = document.createElement('div');
    dayLabel.className = 'mm-notif-day-label';
    dayLabel.textContent = `Día ${day}`;
    group.appendChild(dayLabel);

    acts.forEach(act => {
      const item = document.createElement('div');
      item.className = `mm-notif-item ${act.state === 'past' ? 'is-past' : 'is-upcoming'}`;

      item.innerHTML = `
        <span class="mm-notif-icon" aria-hidden="true">${icon(act.action)}</span>
        <span class="mm-notif-time">${fmt(process.startDate, act.timeOffset)}</span>
        <span class="mm-notif-action">${act.action}</span>
        ${act.state === 'past'
          ? '<span class="mm-notif-sent" aria-label="Enviada">✓</span>'
          : '<span class="mm-notif-dot" aria-hidden="true"></span>'}
      `;

      group.appendChild(item);
    });

    container.appendChild(group);
  });
}

// ── Schedule browser notifications ───────────────────────────-
export async function scheduleNotifications(process) {
  const pData = PROCESSES[process.type];
  if (!pData) return;

  _saveNotificationsForProcess(process.id, pData.activities, process.startDate);
  
  if (Capacitor.isNativePlatform()) {
    _scheduleAllPendingNative();
    return;
  }

  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    const r = await Notification.requestPermission();
    if (r !== 'granted') return;
  }
  if (Notification.permission !== 'granted') return;

  _scheduleAllPending();
}

// ── Schedule Native Notifications (Capacitor) ────────────────-
async function _scheduleAllPendingNative() {
  try {
    const notifs = _getStoredNotifs().filter(n => !n.fired && n.scheduledAt > Date.now());
    
    // First, clear existing pending to avoid duplicates
    const pending = await LocalNotifications.getPending();
    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel({ notifications: pending.notifications });
    }

    if (notifs.length === 0) return;

    const toSchedule = notifs.map(n => ({
      id: n.id,
      title: `MASAGOTCHI — Día ${n.day} 🍞`,
      body: `${n.emoji || ''} ${n.action}: ${n.description}`.trim(),
      schedule: { at: new Date(n.scheduledAt), allowWhileIdle: true },
      smallIcon: "ic_stat_icon_config_sample",
    }));

    await LocalNotifications.schedule({ notifications: toSchedule });
  } catch (e) {
    console.warn('[MM] Native scheduling error:', e);
  }
}

// ── Schedule Web Notifications ───────────────────────────────-
export function _scheduleAllPending() {
  if (Capacitor.isNativePlatform()) {
    _scheduleAllPendingNative();
    return;
  }

  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  const notifs = _getStoredNotifs();
  const now = Date.now();

  notifs.forEach(notif => {
    if (notif.fired) return;
    
    const delay = notif.scheduledAt - now;
    if (delay <= 0) {
      _fireNotification(notif, true);
      return;
    }

    setTimeout(() => {
      _fireNotification(notif, true);
    }, delay);
  });
}

function _fireNotification(notif, markFired = false) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  new Notification(`MASAGOTCHI — Día ${notif.day} 🍞`, {
    body:    `${notif.emoji || ''} ${notif.action}: ${notif.description}`.trim(),
    icon:    '/assets/logo.png',
    tag:     notif.id.toString(),
    vibrate: [200, 100, 200],
    badge:   '/assets/logo.png',
  });

  if (markFired) {
    const notifs = _getStoredNotifs();
    const idx = notifs.findIndex(n => n.id === notif.id);
    if (idx !== -1) {
      notifs[idx].fired = true;
      _saveStoredNotifs(notifs);
    }
  }
}

// ── Clear notifications for a process ────────────────────────
export async function clearNotificationsForProcess(processId) {
  const notifs = _getStoredNotifs();
  const toKeep = notifs.filter(n => n.processId !== processId);
  const toCancel = notifs.filter(n => n.processId === processId && !n.fired);
  
  _saveStoredNotifs(toKeep);

  if (Capacitor.isNativePlatform() && toCancel.length > 0) {
    try {
      await LocalNotifications.cancel({
        notifications: toCancel.map(n => ({ id: n.id }))
      });
    } catch (e) {
      console.warn('[MM] Native cancel error:', e);
    }
  }
}
